import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'store/hooks';
import { BudgetExpenseOrIncome } from 'business/constants';
import { useGetBudgetProposals } from 'routes/BudgetModule/helpers/hooks/budgetProposals';
import Loading from 'components/Loading';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import NotFound from 'components/Errors/NotFound';
import Forbidden from 'components/Errors/Forbidden';
import OperationHeader from './OperationHeader';
import OperationFooter from './OperationFooter';
import OperationBalances from './OperationBalances/OperationBalances';
import {
    useGetEnrichedOperation,
    useGetOperationAssociations,
    useGetOperationInfo,
} from './helpers/hooks';
import OperationInscriptions from './OperationInscriptions/OperationInscriptions';
import OperationDescription from './OperationDescription';
import OperationBudgetProposalByResource from './OperationBudgetProposalByResource';
import {
    getBudgetByFromListOfBudgetProposal,
    getExercicesOfBudget,
} from '../shared/BudgetLibrary/helper';

const StyBox = styled(Box, {
    name: 'StyBox',
})({
    minHeight: '90%',
    display: 'flex',
    margin: '0rem 3rem',
    flexDirection: 'column',
});

export default function Operation360(): React.ReactElement {
    const {
        operationSoftwareId = '',
        expenseOrIncome = BudgetExpenseOrIncome.EXPENSE.toLowerCase(),
    } = useParams();

    const userId = useSelector((vision) => vision.vision.user.id);

    const { data: budgetProposalResponse, isLoading: isBudgetProposalLoading } =
        useGetBudgetProposals();
    const budgetProposals = useMemo(
        () => budgetProposalResponse || [],
        [budgetProposalResponse],
    );

    // If no exercices are specified,
    // we take the highest one from the budget proposals
    const selectedExercice = useMemo(() => {
        const exercices = getExercicesOfBudget(budgetProposals);
        return Math.max(-1, ...exercices);
    }, [budgetProposals]);

    const budgetBy = useMemo(() => {
        if (!budgetProposalResponse) return null;
        return getBudgetByFromListOfBudgetProposal(
            budgetProposals,
            selectedExercice,
        );
    }, [budgetProposals, selectedExercice]);

    const {
        data: operationInfo,
        isLoading: isOperationLoading,
        error: operationInfoError,
    } = useGetOperationInfo(operationSoftwareId);

    const operationName = operationInfo?.name ?? '';

    const {
        data: budgetProposalByResource,
        isLoading: areBpsByResourceLoading,
    } = useGetOperationAssociations(operationSoftwareId, budgetBy);

    const {
        data: enrichedOperation,
        isLoading: isEnrichedOperationLoading,
        error: enrichedOperationError,
    } = useGetEnrichedOperation(operationSoftwareId, budgetBy);

    const isLoading =
        isBudgetProposalLoading ||
        isOperationLoading ||
        areBpsByResourceLoading ||
        isEnrichedOperationLoading ||
        !budgetBy;

    const header = useMemo(
        () => (
            <OperationHeader
                operationName={operationName}
                exercice={selectedExercice}
            />
        ),
        [operationName, selectedExercice],
    );

    const operationBalances = useMemo(() => {
        if (operationInfo === undefined || operationInfo.isDraft || !budgetBy) {
            return <></>;
        }
        return (
            <OperationBalances
                operationSoftwareId={operationSoftwareId}
                budgetProposals={budgetProposals}
                exercice={selectedExercice}
                budgetBy={budgetBy}
            />
        );
    }, [
        operationInfo,
        operationSoftwareId,
        budgetProposals,
        selectedExercice,
        budgetBy,
    ]);

    const operationInscriptions = useMemo(() => {
        if (!budgetBy) {
            return <></>;
        }
        return (
            <OperationInscriptions
                operationInfos={{
                    operationSoftwareId,
                    operationName,
                    isDraft: operationInfo?.isDraft,
                }}
                budgetProposals={budgetProposals}
                expenseOrIncome={
                    expenseOrIncome.toUpperCase() as BudgetExpenseOrIncome
                }
                budgetBy={budgetBy}
                budgetProposalResources={budgetProposalByResource ?? []}
            />
        );
    }, [
        operationSoftwareId,
        operationName,
        operationInfo?.isDraft,
        budgetProposals,
        expenseOrIncome,
        selectedExercice,
        budgetBy,
        userId,
        budgetProposalByResource,
    ]);

    const description = useMemo(
        () =>
            enrichedOperation && (
                <OperationDescription enrichedOperation={enrichedOperation} />
            ),
        [
            enrichedOperation,
            userId,
            budgetBy,
            budgetProposalByResource,
            budgetProposals,
        ],
    );

    const bpByResource = useMemo(() => {
        if (!budgetBy) {
            return <></>;
        }
        return (
            <OperationBudgetProposalByResource
                budgetBy={budgetBy}
                budgetProposalResources={budgetProposalByResource ?? []}
            />
        );
    }, [budgetBy, budgetProposals, budgetProposalByResource]);
    const footer = useMemo(
        () => <OperationFooter operationName={operationName} />,
        [operationName],
    );

    const is404 = operationInfoError?.status === 404;
    const is403 = enrichedOperationError?.status === 403;

    return (
        <div className="page-container dx-viewport">
            {isLoading && <Loading />}
            {is404 && <NotFound />}
            {!isLoading && !operationInfoError && (
                <>
                    {header}
                    <StyBox>
                        {is403 ? (
                            <Forbidden sx={{ margin: 'auto' }} />
                        ) : (
                            <>
                                {description}
                                {bpByResource}
                                {operationBalances}
                                {operationInscriptions}
                            </>
                        )}
                    </StyBox>
                    {footer}
                </>
            )}
        </div>
    );
}
